import Constants from '@/Constants';
import { ClassEnrollment } from '@/models/ClassEnrollment';
import { CourseEnrollment } from '@/models/CourseEnrollment';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import List from '@/models/List';
import { ProgramClass } from '@/models/ProgramClass';
import { ApplicationState } from '@/store';
import { CourseStudentView } from '@/store/list/types';
import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import { useGet } from './useGet';

interface Props {
    moduleId: number;
    courseId: number;
    classEnrollmentId: number;
}

const getKey = (personId: number, moduleId?: number, courseId?: number, classEnrollmentId?: number): string | null => {
    if (!personId || !moduleId || !courseId || !classEnrollmentId) {
        return null;
    }

    if (moduleId === Constants.LMS_MODULE_ID) {
        return `${Constants.FLOWLMS_COURSE_STUDENT_VIEW_URI}?classEnrollmentId=${classEnrollmentId}`
            .replace('%p', personId.toString())
            .replace('%c', courseId.toString());
    }

    if (moduleId === Constants.PLAYLIST_MODULE_ID) {
        return Constants.COURSE_STUDENT_VIEW_URI.replace('%p', personId.toString()).replace('%c', courseId.toString());
    }

    return null;
};

export const useCourseStudentView = ({ moduleId, courseId, classEnrollmentId }: Props) => {
    const { cache } = useSWRConfig();

    const personId: number = useSelector((state: ApplicationState) => state.identity.data.person.id || 0);

    const { data: courseStudentView = {} as CourseStudentView, loading, hasError, mutate } = useGet<CourseStudentView>(
        getKey(personId, moduleId, courseId, classEnrollmentId),
    );

    const {
        classEnrollment = {} as ClassEnrollment,
        courseEnrollment = {} as CourseEnrollment,
        programClass = {} as ProgramClass,
        course = {} as List,
    } = courseStudentView;

    const getLessonEnrollment = (lessonId: number): LessonEnrollment => {
        const emptyLessonEnrollment = {} as LessonEnrollment;
        const { lessonEnrollments = [] } = courseEnrollment;
        return lessonEnrollments.find(({ lesson }) => lesson.id === lessonId) || emptyLessonEnrollment;
    };

    const clearCourseStudentView = (): void => {
        cache.delete(getKey(personId, moduleId, courseId, classEnrollmentId));
    };

    const refresh = (courseStudentViewAux: CourseStudentView = courseStudentView, revalidate: boolean = true) => {
        mutate(courseStudentViewAux, revalidate);
    };

    return {
        courseStudentView,
        classEnrollment,
        courseEnrollment,
        programClass,
        course,
        loading,
        hasError,
        mutate,
        getLessonEnrollment,
        clearCourseStudentView,
        refresh,
    };
};
